import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchPathologyQueue } from "../../store/slices/pathologistResultSlice";

const formatDateTime = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
};

const PathologistQueue = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { queue = [], loading, error } = useSelector((s) => s.pathologistReports || {});

  const primaryBtn =
    "inline-flex items-center justify-center gap-2 rounded-xl bg-[#235F72] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1a4a5a] focus:outline-none focus:ring-2 focus:ring-[#235F72] focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed";
  const secondaryBtn =
    "inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#235F72] focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed";
  const linkBtn = "text-sm font-semibold text-[#235F72] hover:underline";

  useEffect(() => {
    dispatch(fetchPathologyQueue());
  }, [dispatch]);

  return (
    <div className="mx-auto w-full max-w-6xl p-4 sm:p-6">
      <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70">
        <div className="border-b border-gray-200/70 bg-gradient-to-r from-gray-50 to-white px-5 py-4 sm:px-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#235F72]/10 px-3 py-1 text-xs font-semibold text-[#235F72] ring-1 ring-[#235F72]/20">
                Pathology
              </div>
              <h2 className="mt-2 text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">
                Pathologist Queue
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Orders ready for reporting and verification.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button onClick={() => dispatch(fetchPathologyQueue())} className={secondaryBtn} disabled={loading}>
                {loading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-400/80 border-t-transparent" />
                    Refreshing…
                  </>
                ) : (
                  "Refresh"
                )}
              </button>
              <button onClick={() => dispatch(fetchPathologyQueue())} className={primaryBtn} disabled={loading}>
                Open latest
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="border-b border-gray-200/70 bg-red-50 px-5 py-4 text-sm text-red-800 sm:px-6">
            {String(error)}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
              <tr>
                <th className="px-4 py-3 sm:px-6">Order</th>
                <th className="px-4 py-3 sm:px-6">Patient</th>
                <th className="px-4 py-3 sm:px-6">Test</th>
                <th className="px-4 py-3 sm:px-6">Priority</th>
                <th className="px-4 py-3 sm:px-6">Ordered</th>
                <th className="px-4 py-3 sm:px-6">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(Array.isArray(queue) ? queue : []).map((o) => (
                <tr key={o.id} className="hover:bg-gray-50/60">
                  <td className="px-4 py-3 font-semibold text-gray-900 sm:px-6">#{o.id}</td>
                  <td className="px-4 py-3 text-gray-800 sm:px-6">{o.patient_name || "-"}</td>
                  <td className="px-4 py-3 text-gray-800 sm:px-6">{o.test_name || "-"}</td>
                  <td className="px-4 py-3 sm:px-6">
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700 ring-1 ring-gray-200">
                      {String(o.priority || "normal")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700 sm:px-6">{formatDateTime(o.ordered_at)}</td>
                  <td className="px-4 py-3 sm:px-6">
                    <button className={linkBtn} onClick={() => navigate(`/pathologist/morphology/${o.id}`)}>
                      Open
                    </button>
                  </td>
                </tr>
              ))}

              {(!queue || queue.length === 0) && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500 sm:px-6">
                    {loading ? "Loading queue…" : "No orders in queue."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PathologistQueue;
